/**
 * EditRecordDialog — admin dialog for editing an inventory record (product or prep).
 */
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Check } from "@phosphor-icons/react";

export function EditRecordDialog({
  editingRecord, setEditingRecord,
  editRecordForm, setEditRecordForm,
  locations, onSave
}) {
  return (
    <Dialog open={!!editingRecord} onOpenChange={(open) => !open && setEditingRecord(null)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-wide">
            EDIT RECORD — {editingRecord?.item_name || editingRecord?.product_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2">
            <Badge variant={editingRecord?.item_type === "prep" ? "secondary" : "outline"} className="text-[10px]">
              {editingRecord?.item_type === "prep" ? "PREP ITEM" : "PRODUCT"}
            </Badge>
            <span className="text-sm text-muted-foreground">{editingRecord?.unit || "CS"}</span>
          </div>

          {/* Date & Location Edit (Admin) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={editRecordForm.recorded_at}
                onChange={(e) => setEditRecordForm(p => ({ ...p, recorded_at: e.target.value }))}
                data-testid="edit-record-date"
              />
            </div>
            <div>
              <Label className="text-xs">Location</Label>
              <Select value={editRecordForm.location_id} onValueChange={(val) => setEditRecordForm(p => ({ ...p, location_id: val }))}>
                <SelectTrigger data-testid="edit-record-location">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc.location_id} value={loc.location_id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quantity fields - different for product vs prep */}
          {editingRecord?.item_type === "prep" ? (
            <div>
              <Label className="text-xs">Quantity (Pans/Batches)</Label>
              <Input
                type="number"
                value={editRecordForm.count_ea}
                onChange={(e) => setEditRecordForm(p => ({ ...p, count_ea: parseFloat(e.target.value) || 0 }))}
                min="0" step="0.5"
                data-testid="edit-prep-quantity"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cases (CS)</Label>
                <Input
                  type="number"
                  value={editRecordForm.count_cs}
                  onChange={(e) => setEditRecordForm(p => ({ ...p, count_cs: parseFloat(e.target.value) || 0 }))}
                  min="0" step="0.01"
                  data-testid="edit-count-cs"
                />
              </div>
              <div>
                <Label className="text-xs">Each (EA)</Label>
                <Input
                  type="number"
                  value={editRecordForm.count_ea}
                  onChange={(e) => setEditRecordForm(p => ({ ...p, count_ea: parseFloat(e.target.value) || 0 }))}
                  min="0" step="0.01"
                  data-testid="edit-count-ea"
                />
              </div>
              <div>
                <Label className="text-xs">Pounds (LB)</Label>
                <Input
                  type="number"
                  value={editRecordForm.count_lb}
                  onChange={(e) => setEditRecordForm(p => ({ ...p, count_lb: parseFloat(e.target.value) || 0 }))}
                  min="0" step="0.01"
                />
              </div>
              <div>
                <Label className="text-xs">Ounces (OZ)</Label>
                <Input
                  type="number"
                  value={editRecordForm.count_oz}
                  onChange={(e) => setEditRecordForm(p => ({ ...p, count_oz: parseFloat(e.target.value) || 0 }))}
                  min="0" step="0.01"
                />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs">Notes</Label>
            <Input
              value={editRecordForm.notes}
              onChange={(e) => setEditRecordForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Optional notes"
            />
          </div>
          {editingRecord?.audit_trail && editingRecord.audit_trail.length > 0 && (
            <div className="text-xs text-muted-foreground border-t pt-2">
              <p className="font-medium mb-1">Edit History:</p>
              {editingRecord.audit_trail.map((entry, i) => (
                <p key={entry.edited_at || i}>{entry.edited_at?.slice(0, 10)} - {entry.changes?.map(c => `${c.field}: ${c.old_value} → ${c.new_value}`).join(', ')}</p>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => setEditingRecord(null)}>Cancel</Button>
          <Button onClick={onSave} className="bg-primary" data-testid="save-record-btn">
            <Check size={16} className="mr-2" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
